import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Grid, Typography, Loader } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';

// constants
import { API } from 'constants/api';

// selectors
import {
  getItemCodes,
  getBulkCodesErrorFiles,
  getIsFetchingErrorFiles,
  getIsFileUploaded,
} from 'ducks/merchantDucks/items/selectors';

// types
import { ErrorFile } from 'ducks/merchantDucks/vouchers/types';

// actions
import { getBulkCodeTemplate, uploadCodeFile, getBulkErrorFiles } from 'ducks/merchantDucks/items/actions';
import { downloadErrorFile } from 'ducks/merchantDucks/common/api';

// components
import { CustomButton } from 'pages/MerchantPages/Assets/Price/containers/PriceOptions';
import {
  BlueTypography,
  FileContainer,
  FlexContainer,
  StyledIcon,
  StyledSpan,
  ItalicDescription,
} from 'components/SharedStyledElements';
import UploadFile from 'components/UploadFile';
import { LoaderContainer } from 'pages/MerchantPages/Assets/Packages/ManageAssets/DroppableColumn';

const ErrorsTitle = styled(BlueTypography)`
  margin-bottom: 1rem;
`;

const { Cell, Container } = Grid;

interface Props {
  handleBackToCodesTable: () => void;
}

const AddInBulkForm: FunctionComponent<Props> = ({ handleBackToCodesTable }: Props) => {
  const dispatch = useDispatch();
  const { id: itemId } = useParams<{ id: string }>();
  // Show explain message only after file is successfully dropped and uploaded
  const [isFileDropped, setIsFileDropped] = useState(false);

  const { isCreating } = useSelector(getItemCodes);
  const errorFiles = useSelector(getBulkCodesErrorFiles);
  const isFetchingErrorFiles = useSelector(getIsFetchingErrorFiles);
  const isFileUploaded = useSelector(getIsFileUploaded);

  useEffect(() => {
    dispatch(getBulkErrorFiles());
  }, [getBulkErrorFiles]);

  const handleDownloadCodesTemplate = () => {
    dispatch(getBulkCodeTemplate());
  };

  const onCodesFileDrop = async (file: File) => {
    await dispatch(uploadCodeFile(file, Number(itemId)));
    setIsFileDropped(true);
  };

  const downloadFile = async (fileName: string) => {
    const endpoint = API.DOWNLOAD_BULK_FAILED_FILE;
    await downloadErrorFile({ endpoint, fileName });
  };

  const renderErrorFileList = () => {
    if (isFetchingErrorFiles) {
      return (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      );
    }

    if (errorFiles.length === 0) {
      return <Typography variant="p">There are no error files.</Typography>;
    }

    return (
      <>
        <FlexContainer isMarginBottom>
          <Typography variant="p">
            The below files contain passcodes that could not be created. The files will be stored for 7 days after being
            created and then automatically deleted.
          </Typography>
        </FlexContainer>
        <div>
          {errorFiles.map((file: ErrorFile) => {
            const { name, createdAt } = file;
            return (
              <FileContainer onClick={() => downloadFile(name)} key={name}>
                <StyledSpan>{name}</StyledSpan>
                <span>{createdAt}</span>
                <StyledIcon />
              </FileContainer>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <Container columns={4} rowGap="2rem">
      <Cell middle>
        <CustomButton buttonModifiers={['buttonLink']} type="button" onClick={handleBackToCodesTable}>
          &larr; back
        </CustomButton>
      </Cell>
      <Cell width={4}>
        <FlexContainer>
          <Typography variant="p">
            {`Create multiple passcodes by uploading them in a csv file. When uploading, make sure to use `}
            <StyledSpan isColor onClick={handleDownloadCodesTemplate}>
              this template
            </StyledSpan>
            .
          </Typography>
        </FlexContainer>
      </Cell>
      <Cell width={4}>
        {isCreating ? (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        ) : (
          <UploadFile uploadImage={onCodesFileDrop} />
        )}
      </Cell>
      {isFileUploaded && isFileDropped && (
        <Cell width={4}>
          <ItalicDescription variant="p">
            The codes are successfully uploaded for this asset. You can check the error file below to see if some codes
            were not uploaded due to an error. If you do not see an error files, it means all the codes were imported.
          </ItalicDescription>
        </Cell>
      )}
      <Cell width={4}>
        <ErrorsTitle variant="h6">Error files</ErrorsTitle>
        {renderErrorFileList()}
      </Cell>
    </Container>
  );
};

export default AddInBulkForm;
